import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, FormBuilder, FormArray } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { faChevronLeft, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { ControllerForViewBase } from '../framework/controller-for-view-base';
import {
  ClientDto,
  AssetDto,
  ProjectDto,
  ProjectSetupDto,

  GetProjectsByUserCommandResponse,
  GetProjectSetupCommandRequest,
  GetProjectSetupCommandResponse,
  PersistProjectSetupCommandRequest,
  PersistProjectSetupCommandResponse,
  GetClientAndAssetListByUserCommandResponse,
} from "../../data/CommandServiceDtos.generated";
import { PageContainerService } from '../../services';


@Component({
  selector: 'new-project',
  templateUrl: './new-project.component.html',
  styleUrls: ['./new-project.component.scss']
})
export class NewProjectComponent implements OnInit {
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  faChevronLeft = faChevronLeft;
  iconExecutionLink = faClipboardList;
  isEditing: boolean = false;
  titleValue: string = "Add New Project";
  buttonValue: string = "Add Project";
  clientControl = new FormControl();
  assetControl = new FormControl();

  clientList: ClientDto[] = [];
  assetList: AssetDto[] = [];
  cachedAssets: AssetDto[] = [];
  //Material chip settings
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;

  clientIds: string[] = [];
  siteSenseIds: string[] = [];
  primaveraId: string = '';
  cxPlannerIds: string[] = [];
  projectId: any;
  
  project: ProjectSetupDto = new ProjectSetupDto();

  constructor(public pcs: PageContainerService, public route: ActivatedRoute) {
  }

  ngOnInit() {
    this.loadDropdownData().then(() => {

      if (this.route.routeConfig.path.includes('edit')) {
        //in edit mode grab the details
        this.isEditing = true;
        this.titleValue = "Edit Existing Project";
        this.buttonValue = "Save Project";
        let id = +this.route.snapshot.paramMap.get('id');
        this.projectId = id;
        this.loadProjectData(id);
      } else {
        //in create mode set default values
        this.isEditing = false;
        this.assetControl.setValue(0);
        this.clientControl.setValue(0);
      }
    });
  }

  async loadProjectData(id: number) {
    const request: GetProjectSetupCommandRequest = { ProjectId: id }
    const response: GetProjectSetupCommandResponse = await this.pcs.apiService.GetProjectSetup(request, true, false);
    this.setProjectSetup(response);
  }

  setProjectSetup(response: GetProjectSetupCommandResponse) {
    let _rProject: ProjectSetupDto = response.ProjectInfo;

    this.project.Id = _rProject.Id;
    this.primaveraId = _rProject.PrimaveraIdentifiers[0];
    this.project.ReadinessAssessmentPath = _rProject.ReadinessAssessmentPath;
    this.project.ProPakProject.Name = _rProject.ProPakProject.Name;
    this.clientIds = _rProject.ClientIdentifiers;
    this.siteSenseIds = _rProject.SiteSenseIdentifiers;
    this.clientControl.setValue(_rProject.ProPakProject.Client);
    this.assetList = this.cachedAssets.filter((item) => item.Client.ClientId === _rProject.ProPakProject.Client.ClientId);
    this.assetControl.setValue(_rProject.ProPakProject.Asset);
    this.cxPlannerIds = _rProject.CXPlannerIdentifiers;
    this.project.PowerBiReportPath = _rProject.PowerBiReportPath;
    this.project.ProjectDisplayType = _rProject.ProjectDisplayType;
    this.project.ShowProjected = _rProject.ShowProjected;
    this.project.MasterReport = _rProject.MasterReport;
    this.project.ShowFullSchedule = _rProject.ShowFullSchedule;
    this.project.ActivitiesDisplayType = _rProject.ActivitiesDisplayType;
  }

  //public int Id { get; set; }
  //      public ProjectDto ProPakProject { get; set; }
  //      public string ReadinessAssessmentPath { get; set; }
  //      public ICollection < string > PrimaveraIdentifiers { get; set; }
  //      public ICollection < string > SiteSenseIdentifiers { get; set; }
  //      public ICollection < string > ClientIdentifiers { get; set; }

  async loadDropdownData() {
    const getCommandResponse: GetClientAndAssetListByUserCommandResponse = await this.pcs.apiService.GetClientAndAssetListByUser({});
    this.mapResponseToModel(getCommandResponse);
  }

  mapResponseToModel(getCommandResponse: GetClientAndAssetListByUserCommandResponse) {
    this.clientList = getCommandResponse.ClientList;
    this.cachedAssets = getCommandResponse.AssetList;
  }

  clientChanged() {
    const clientId = this.clientControl.value.ClientId;
    if (clientId > 0) {
      this.assetList = this.cachedAssets.filter((item) => item.Client.ClientId === clientId);
      if (this.assetList.length === 1) this.assetControl.setValue(this.assetList[0]);
    }
    else {
      this.assetList = [];
    }
  }

  saveProject(form) {
    this.project.ProPakProject.Asset = this.assetControl.value;
    this.project.ProPakProject.Client = this.clientControl.value;
    this.project.SiteSenseIdentifiers = this.siteSenseIds;
    this.project.ClientIdentifiers = this.clientIds;
    this.project.PrimaveraIdentifiers[0] = this.primaveraId;
    this.project.CXPlannerIdentifiers = this.cxPlannerIds;

    let request: PersistProjectSetupCommandRequest = { ProjectInfo: this.project };
    this.sendProjectRequest(request);
  }

  async sendProjectRequest(request: PersistProjectSetupCommandRequest) {
    const response: PersistProjectSetupCommandResponse =
      await this.pcs.apiService.PersistProjectSetup(request, true, false);
    this.processSaveResponse(response);
  }

  processSaveResponse(response: PersistProjectSetupCommandResponse) {
    if (response.ProjectInfo.Id > 0)
      this.successRedirect();
  }

  successRedirect() {
    let message: string =
      (this.isEditing) ? "Project Updated" : "Project Saved";
    this.pcs.notificationService.success(message, '', {
      timeOut: 3000, onHidden: () => {
        this.pcs.router.navigate(['/projects']);
      }
    });
  }

  cancel() {
    this.pcs.router.navigate(['/projects']);
  }

  formValid(): boolean {
    if (this.clientControl.value === 0 || this.assetControl.value === 0)
      return false;
    else
      return true;
  }

  clientCompareFn(c1: ClientDto, c2: ClientDto) {
    return c1 && c2 ? c1.ClientId === c2.ClientId : c1 === c2;
  }

  assetCompareFn(a1: AssetDto, a2: AssetDto) {
    return a1 && a2 ? a1.AssetId === a2.AssetId : a1 === a2;
  }

  addClient(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value.trim();

    if (value !== '') {
      this.clientIds.push(value);
    }

    if (input) {
      input.value = '';
    }
  }

  addSiteSense(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {

      this.siteSenseIds.push(value.trim());
    }

    if (input) {
      input.value = '';
    }
  }

  removeClient(client): void {
    let index = this.clientIds.indexOf(client);

    if (index >= 0) {
      this.clientIds.splice(index, 1);
    }
  }

  removeSiteSense(item): void {
    let index = this.siteSenseIds.indexOf(item);

    if (index >= 0) {
      this.siteSenseIds.splice(index, 1);
    }
  }

  addCXPlanner(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {

      if (this.cxPlannerIds.length < 1)
        this.cxPlannerIds.push(value.trim());
      else
        this.pcs.notificationService.warning("Only one CXPlanner Project can be linked with this project.");
    }

    if (input) {
      input.value = '';
    }
  }

  removeCXPlanner(item): void {
    let index = this.cxPlannerIds.indexOf(item);

    if (index >= 0) {
      this.cxPlannerIds.splice(index, 1);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/new-project/new-project.component.ts